import React, { useCallback, useEffect, useState } from "react";
import PreviewFonts from "./PreviewFonts";
import SectionBoxWrapper from "../../SectionBox/SectionBoxWrapper";
import PreviewBox from "../../SectionBox/PreviewBox";
import FontSelection from "..";

export interface SectionBoxFontsProps {
  themeConfig: any;
  onSubmit: any;
}

/* The font, its weight and the extra font size are kept in one api field
   ("Poppins*600+2"), so the stored value is split when it is read and joined
   again when it is saved. */
const splitFontValue = (fontValue: any) => {
  const [font = "", extra = ""] = String(fontValue || "").split("*");
  const [weight = "", size = ""] = String(extra).split("+");
  return { font: font.trim(), weight: weight.trim(), size: size.trim() };
};

const joinFontValue = (font: any, weight: any, size: any) => {
  // Only a number (or, for the weight, a word) is saved, so a stray * or +
  // can't break the split. A legacy weight name saved before the dropdown
  // existed ("normal", "bold") is kept as it is, so it isn't lost on save.
  const weightValue = String(weight || "").trim();
  const sizeValue = String(size || "").trim();
  const trimmedWeight = /^(\d+|[a-zA-Z]+)$/.test(weightValue) ? weightValue : "";
  const trimmedSize = /^-?\d+$/.test(sizeValue) ? sizeValue : "";
  if (!font || (!trimmedWeight && !trimmedSize)) return font;
  return `${font}*${trimmedWeight}${trimmedSize ? `+${trimmedSize}` : ""}`;
};

/* Update heading/body font selection and preview live font changes. */
const SectionBoxFonts: React.FunctionComponent<SectionBoxFontsProps> = ({
  themeConfig,
  onSubmit,
}) => {
  const [headingFont, setHeadingFont] = useState(""); // maps to API field: globalFont
  const [bodyFont, setBodyFont] = useState(""); // maps to API field: highlightFont
  // The one font weight and extra font size of the section, sent with the
  // highlight font.
  const [fontWeight, setFontWeight] = useState("");
  const [fontSize, setFontSize] = useState("");
  const [initialized, setInitialized] = useState(false);

  const setInitialData = useCallback(() => {
    if (headingFont) return;
    const { globalFont, highlightFont } = themeConfig;

    const globalFontValue = splitFontValue(globalFont);
    const highlightFontValue = splitFontValue(highlightFont);

    setHeadingFont(globalFontValue.font || "");
    setFontWeight(globalFontValue.weight || "");
    setFontSize(globalFontValue.size || "");
    setBodyFont(highlightFontValue.font || "");
    setInitialized(true);
  }, [headingFont, themeConfig]);
  useEffect(() => {
    setInitialData();
  }, [setInitialData]);

  const handleSave = () => {
    onSubmit({
      globalFont: joinFontValue(headingFont, fontWeight, fontSize),
      highlightFont: bodyFont,
    });
  };

  const handleCancel = () => {
    const globalFontValue = splitFontValue(themeConfig.globalFont);
    const highlightFontValue = splitFontValue(themeConfig.highlightFont);

    setHeadingFont(globalFontValue.font || "");
    setFontWeight(globalFontValue.weight || "");
    setFontSize(globalFontValue.size || "");
    setBodyFont(highlightFontValue.font || "");
  };
  return (
    <SectionBoxWrapper
      sectionTitle={"Fonts"}
      onSubmit={handleSave}
      onCancel={handleCancel}
      colorChangeSection={
        <>
          <FontSelection
            title={"Global Font"}
            value={bodyFont}
            setValue={setBodyFont}
            previewText={"How would you like to place your order?"}
          />
          <FontSelection
            title={"Highlight Font"}
            value={headingFont}
            setValue={setHeadingFont}
            previewText={"Select Order Type"}
            fontWeight={fontWeight}
            setFontWeight={setFontWeight}
            fontSize={fontSize}
            setFontSize={setFontSize}
          />
        </>
      }
      previewSection={
        <PreviewBox bgColor={themeConfig.menuBackgroundColor}>
          <PreviewFonts
            headingFont={bodyFont}
            bodyFont={headingFont}
            textColor={themeConfig.titleTextColor}
            bodyFontWeight={fontWeight}
            bodyFontSize={fontSize}
          />
        </PreviewBox>
      }
    />
  );
};

export default SectionBoxFonts;
